import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchData, postData, deleteData } from '../services/api';
import type { Child } from '@shared/types';

export default function Children() {
  const [showAddChild, setShowAddChild] = useState(false);
  const [selectedChild, setSelectedChild] = useState<Child | null>(null);

  const { data: children, isLoading } = useQuery({
    queryKey: ['children'],
    queryFn: () => fetchData<Child[]>('/children'),
  });

  return (
    <div className="space-y-6 pb-20 lg:pb-0">
      {/* Header */}
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-neutral-900 dark:text-neutral-100 flex items-center gap-2">
            <span className="text-warmth-500">❤️</span>
            Children
          </h1>
          <p className="text-neutral-500 dark:text-neutral-400 mt-1">
            Your most important priority
          </p>
        </div>
        <button
          onClick={() => setShowAddChild(true)}
          className="btn-warmth"
        >
          <PlusIcon />
          <span className="hidden sm:inline">Add Child</span>
        </button>
      </header>

      {/* Today's Overview */}
      <section className="card child-related">
        <h2 className="text-lg font-semibold mb-4">Today's Schedule</h2>
        <TodaySchedule children={children || []} />
      </section>

      {/* Children List */}
      <section>
        {isLoading ? (
          <div className="space-y-4">
            {[1, 2].map((i) => (
              <div key={i} className="card">
                <div className="flex items-center gap-4">
                  <div className="skeleton w-16 h-16 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <div className="skeleton h-5 w-32" />
                    <div className="skeleton h-4 w-48" />
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : children && children.length > 0 ? (
          <div className="space-y-4">
            {children.map((child) => (
              <ChildDetailCard
                key={child.id}
                child={child}
                onSelect={() => setSelectedChild(child)}
              />
            ))}
          </div>
        ) : (
          <EmptyChildrenState onAdd={() => setShowAddChild(true)} />
        )}
      </section>

      {/* Add Child Modal */}
      {showAddChild && (
        <AddChildModal onClose={() => setShowAddChild(false)} />
      )}

      {/* Child Detail Modal */}
      {selectedChild && (
        <ChildDetailModal
          child={selectedChild}
          onClose={() => setSelectedChild(null)}
        />
      )}
    </div>
  );
}

function TodaySchedule({ children }: { children: Child[] }) {
  const today = new Date();
  const dayOfWeek = today.toLocaleDateString('en-US', { weekday: 'long' });

  if (children.length === 0) {
    return (
      <p className="text-neutral-500 dark:text-neutral-400 text-center py-4">
        Add children to see custody schedule
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 p-4 rounded-soft bg-warmth-100 dark:bg-warmth-900/30">
        <div className="text-3xl">🏠</div>
        <div>
          <p className="font-medium text-warmth-800 dark:text-warmth-200">
            {dayOfWeek}
          </p>
          <p className="text-sm text-warmth-600 dark:text-warmth-400">
            Children are with you today
          </p>
        </div>
      </div>

      {/* Upcoming events preview */}
      <div className="space-y-2">
        <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 uppercase tracking-wide">
          Upcoming
        </h3>
        <div className="text-sm text-neutral-600 dark:text-neutral-400">
          No upcoming events today
        </div>
      </div>
    </div>
  );
}

function ChildDetailCard({ child, onSelect }: { child: Child; onSelect: () => void }) {
  const age = calculateAge(child.dateOfBirth);

  return (
    <div
      onClick={onSelect}
      className="card-interactive"
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onSelect()}
    >
      <div className="flex items-center gap-4">
        <div className="w-16 h-16 rounded-full bg-warmth-200 dark:bg-warmth-800 flex items-center justify-center text-2xl font-bold text-warmth-700 dark:text-warmth-300">
          {child.name.charAt(0)}
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-neutral-100">
            {child.name}
          </h3>
          <p className="text-neutral-500 dark:text-neutral-400">
            {age} years old
          </p>
          {child.school && (
            <p className="text-sm text-neutral-500 dark:text-neutral-400 truncate">
              📚 {child.school.name}
            </p>
          )}
        </div>
        <svg className="w-5 h-5 text-neutral-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
        </svg>
      </div>

      {/* Quick info badges */}
      <div className="flex flex-wrap gap-2 mt-4">
        {child.healthInfo?.allergies && child.healthInfo.allergies.length > 0 && (
          <span className="badge bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
            ⚠️ Allergies
          </span>
        )}
        {child.healthInfo?.medications && child.healthInfo.medications.length > 0 && (
          <span className="badge bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
            💊 Medications
          </span>
        )}
        {child.custodySchedule?.ofwLinked && (
          <span className="badge bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300">
            ✓ OFW Linked
          </span>
        )}
      </div>
    </div>
  );
}

function calculateAge(dateOfBirth: Date): number {
  const today = new Date();
  const birthDate = new Date(dateOfBirth);
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  return age;
}

function EmptyChildrenState({ onAdd }: { onAdd: () => void }) {
  return (
    <div className="card text-center py-12">
      <div className="text-6xl mb-4">👶</div>
      <h3 className="text-xl font-semibold text-neutral-900 dark:text-neutral-100 mb-2">
        No children added yet
      </h3>
      <p className="text-neutral-500 dark:text-neutral-400 mb-6 max-w-sm mx-auto readable-text">
        Add your children to track custody schedules, school events, health information, and more.
      </p>
      <button onClick={onAdd} className="btn-warmth">
        <PlusIcon />
        Add Your First Child
      </button>
    </div>
  );
}

function AddChildModal({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [formData, setFormData] = useState({
    name: '',
    dateOfBirth: '',
    schoolName: '',
    notes: '',
  });

  const mutation = useMutation({
    mutationFn: (data: Partial<Child>) => postData<Partial<Child>, Child>('/children', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      onClose();
    },
  });

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    mutation.mutate({
      name: formData.name,
      dateOfBirth: new Date(formData.dateOfBirth),
      school: formData.schoolName ? { name: formData.schoolName, address: '', phone: '' } : undefined,
      notes: formData.notes,
      emergencyContacts: [],
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-white dark:bg-surface-darker rounded-t-2xl sm:rounded-2xl p-6 safe-bottom">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold">Add Child</h2>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-soft">
            <CloseIcon />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Name *
            </label>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="input"
              required
              autoFocus
            />
          </div>

          <div>
            <label htmlFor="dob" className="block text-sm font-medium mb-2">
              Date of Birth *
            </label>
            <input
              type="date"
              id="dob"
              value={formData.dateOfBirth}
              onChange={(e) => setFormData({ ...formData, dateOfBirth: e.target.value })}
              className="input"
              required
            />
          </div>

          <div>
            <label htmlFor="school" className="block text-sm font-medium mb-2">
              School (optional)
            </label>
            <input
              type="text"
              id="school"
              value={formData.schoolName}
              onChange={(e) => setFormData({ ...formData, schoolName: e.target.value })}
              className="input"
              placeholder="School name"
            />
          </div>

          <div>
            <label htmlFor="notes" className="block text-sm font-medium mb-2">
              Notes (optional)
            </label>
            <textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              className="input min-h-[100px]"
              placeholder="Any important notes..."
            />
          </div>

          <div className="flex gap-3 pt-4">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary">
              Cancel
            </button>
            <button
              type="submit"
              className="flex-1 btn-warmth"
              disabled={mutation.isPending}
            >
              {mutation.isPending ? 'Adding...' : 'Add Child'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ChildDetailModal({ child, onClose }: { child: Child; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState<'info' | 'schedule' | 'health'>('info');

  const deleteMutation = useMutation({
    mutationFn: () => deleteData(`/children/${child.id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['children'] });
      onClose();
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative w-full sm:max-w-lg bg-white dark:bg-surface-darker rounded-t-2xl sm:rounded-2xl safe-bottom max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center gap-4 p-6 border-b border-neutral-200 dark:border-neutral-800">
          <div className="w-14 h-14 rounded-full bg-warmth-200 dark:bg-warmth-800 flex items-center justify-center text-2xl font-bold text-warmth-700 dark:text-warmth-300">
            {child.name.charAt(0)}
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-semibold">{child.name}</h2>
            <p className="text-neutral-500 dark:text-neutral-400">
              {calculateAge(child.dateOfBirth)} years old
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-neutral-100 dark:hover:bg-neutral-800 rounded-soft">
            <CloseIcon />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-neutral-200 dark:border-neutral-800">
          {(['info', 'schedule', 'health'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`flex-1 py-3 text-sm font-medium transition-colors ${
                activeTab === tab
                  ? 'text-warmth-600 dark:text-warmth-400 border-b-2 border-warmth-500'
                  : 'text-neutral-500 dark:text-neutral-400'
              }`}
            >
              {tab.charAt(0).toUpperCase() + tab.slice(1)}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'info' && (
            <div className="space-y-4">
              {child.school && (
                <InfoItem
                  icon="📚"
                  label="School"
                  value={child.school.name}
                  subvalue={child.school.grade ? `Grade ${child.school.grade}` : undefined}
                />
              )}
              {child.notes && (
                <InfoItem icon="📝" label="Notes" value={child.notes} />
              )}
              {child.emergencyContacts.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-neutral-500 dark:text-neutral-400 mb-2">
                    Emergency Contacts
                  </h3>
                  {child.emergencyContacts.map((contact, i) => (
                    <div key={i} className="p-3 rounded-soft bg-neutral-100 dark:bg-neutral-800 mb-2">
                      <p className="font-medium">{contact.name}</p>
                      <p className="text-sm text-neutral-500">{contact.relationship} • {contact.phone}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {activeTab === 'schedule' && (
            <div className="space-y-4">
              {child.custodySchedule ? (
                <>
                  <div className="p-4 rounded-soft bg-warmth-100 dark:bg-warmth-900/30">
                    <p className="font-medium text-warmth-800 dark:text-warmth-200">
                      Schedule Type: {child.custodySchedule.type.replace(/-/g, ' ')}
                    </p>
                  </div>
                  {child.custodySchedule.ofwLinked && (
                    <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                      <span>✓</span>
                      <span>Linked to OurFamilyWizard</span>
                    </div>
                  )}
                </>
              ) : (
                <p className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                  No custody schedule configured
                </p>
              )}
            </div>
          )}

          {activeTab === 'health' && (
            <div className="space-y-4">
              {child.healthInfo?.allergies && child.healthInfo.allergies.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-red-600 dark:text-red-400 mb-2">
                    ⚠️ Allergies
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {child.healthInfo.allergies.map((allergy, i) => (
                      <span key={i} className="badge bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300">
                        {allergy}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {child.healthInfo?.medications && child.healthInfo.medications.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-blue-600 dark:text-blue-400 mb-2">
                    💊 Medications
                  </h3>
                  {child.healthInfo.medications.map((med, i) => (
                    <div key={i} className="p-3 rounded-soft bg-blue-50 dark:bg-blue-900/20 mb-2">
                      <p className="font-medium">{med.name}</p>
                      <p className="text-sm text-neutral-600 dark:text-neutral-400">
                        {med.dosage} • {med.frequency}
                      </p>
                    </div>
                  ))}
                </div>
              )}

              {(!child.healthInfo?.allergies?.length && !child.healthInfo?.medications?.length) && (
                <p className="text-center text-neutral-500 dark:text-neutral-400 py-8">
                  No health information recorded
                </p>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-neutral-200 dark:border-neutral-800">
          <button
            onClick={() => deleteMutation.mutate()}
            className="w-full btn btn-ghost text-red-600 dark:text-red-400"
            disabled={deleteMutation.isPending}
          >
            {deleteMutation.isPending ? 'Removing...' : 'Remove Child'}
          </button>
        </div>
      </div>
    </div>
  );
}

function InfoItem({ icon, label, value, subvalue }: { icon: string; label: string; value: string; subvalue?: string }) {
  return (
    <div className="flex items-start gap-3">
      <span className="text-xl">{icon}</span>
      <div>
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{label}</p>
        <p className="font-medium">{value}</p>
        {subvalue && <p className="text-sm text-neutral-500">{subvalue}</p>}
      </div>
    </div>
  );
}

function PlusIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}
